import React, { useState, forwardRef, useRef, useEffect, useCallback } from 'react';
import AdjustmentsDialog from './AdjustmentsDialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import StableMuiDialog from '../../common/StableMuiDialog.js';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask';
import 'react-datepicker/dist/react-datepicker.css';
import WeightsViewRebuilt from './WeightsViewRebuilt';
import { useLocalDataStore, usePersistedDataStore } from '../store';
import dayjs from 'dayjs';
import { useUser } from '../UserContext';
import { useCourtDates } from '../CourtDateProvider';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../fetchConfig/axiosConfig';
import { toast } from '../../common/toast';
import { calculateUnifiedDateRange, processFilterDataToSettings, getEvaluationDate, decodeFilterDateSettings } from '../../common/dateUtils';
import { mergeNegotiationObjWithAdvancedSettings } from './CaseReviewHelpers/mergeNegotiationObjWithAdvancedSettings';
import { useOfferOptionsStore, OFFER_DELIVERY_MODES } from './Offers/offerOptionsStore.js';
import { WORKUP_REPORT_TYPES } from '../../common/mappings.js';
import logger from '../../utils/logger';

// Date range presets
const DATE_RANGE_PRESETS = {
  BEFORE: [
    { value: '6m', label: '6 months before' },
    { value: '12m', label: '12 months before' },
    { value: '18m', label: '18 months before' },
    { value: '24m', label: '24 months before' },
    { value: 'nocutoff', label: 'No Cutoff' },
  ],
  AFTER: [
    { value: '6m', label: '6 months after' },
    { value: '12m', label: '12 months after' },
    { value: '18m', label: '18 months after' },
    { value: '24m', label: '24 months after' },
    { value: 'nolimit', label: 'No Limit' },
  ]
};

// Add styles to document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = `
    .react-datepicker-popper {
      position: absolute !important;
      z-index: 99999 !important;
      inset: auto !important;
      transform: translate3d(0, 0, 0) !important;
      pointer-events: auto;
    }
  `;
  document.head.appendChild(styleElement);
}

// Custom input using input mask
const CustomDateInput = forwardRef(({ value, onClick, onChange, placeholder, onComplete, style }, ref) => (
  <InputMask
    mask="99/99/9999"
    value={value}
    onChange={(e) => {
      onChange(e);
      // Check if the date is complete (all digits filled)
      const inputValue = e.target.value;
      if (inputValue && inputValue.replace(/[-\/]/g, '').length === 8 && onComplete) {
        // Longer delay to allow DOM + calendar logic to settle
        setTimeout(() => onComplete(), 150);
      }
    }}
    placeholder={placeholder}
    maskChar="-"
    alwaysShowMask={true}
  >
    {(inputProps) => (
      <input
        {...inputProps}
        ref={(node) => {
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node; // This actually gives you access to .focus()
          }
        }}
        onClick={onClick}
        style={{
          padding: '8px 12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontSize: '14px',
          width: '100%',
          fontFamily: 'inherit',
          lineHeight: '1.5',
          ...style,
        }}
      />
    )}
  </InputMask>
));

// Wrapper component for the end date input with ref
const EndDateInput = forwardRef((props, ref) => (
  <CustomDateInput {...props} ref={ref} />
));

const OfferOptionsTab = React.memo(function OfferOptionsTab({ draft, setDraft }) {
  const reportType = usePersistedDataStore((state) => state.negotiationObj?.reportType);
  const workupTypesForUi = WORKUP_REPORT_TYPES.filter((r) => r.id !== 'loa' && !r.hideFromUi);

  return (
    <>
      <Typography variant="h6" sx={{ mb: 0 }}>Offer Options</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        These are your defaults for “Send Offers”. Changes require saving.
      </Typography>

      <FormControlLabel
        sx={{ mt: 1, display: 'block' }}
        control={
          <Checkbox
            checked={!!draft?.includeCompsInOffersSpreadsheet}
            onChange={(e) => {
              const on = e.target.checked;
              setDraft((prev) => ({
                ...(prev || {}),
                includeCompsInOffersSpreadsheet: on,
                workupDeliveryMode: on ? (prev?.workupDeliveryMode || OFFER_DELIVERY_MODES.OFFERS_ONLY) : OFFER_DELIVERY_MODES.OFFERS_ONLY,
              }));
            }}
          />
        }
        label="Include comp addresses and workup value columns in spreadsheet"
      />

      <FormControl
        component="fieldset"
        sx={{ mt: 2 }}
        variant="standard"
        disabled={!draft?.includeCompsInOffersSpreadsheet}
      >
        <FormLabel component="legend">Workup delivery</FormLabel>
        <RadioGroup
          value={draft?.workupDeliveryMode || OFFER_DELIVERY_MODES.OFFERS_ONLY}
          onChange={(e) => setDraft((prev) => ({ ...(prev || {}), workupDeliveryMode: e.target.value }))}
        >
          <FormControlLabel value={OFFER_DELIVERY_MODES.OFFERS_ONLY} control={<Radio />} label="Offers only (no workup pdfs included)" />
          <FormControlLabel value={OFFER_DELIVERY_MODES.INDIVIDUAL} control={<Radio />} label="Individual — each workup has its own link in offers spreadsheet" />
          <FormControlLabel value={OFFER_DELIVERY_MODES.BATCH} control={<Radio />} label="Batch — one combined PDF in email" />
        </RadioGroup>
      </FormControl>

      <FormControl
        size="small"
        fullWidth
        sx={{ mt: 2, maxWidth: 420 }}
        disabled={
          !draft?.includeCompsInOffersSpreadsheet ||
          (draft?.workupDeliveryMode !== OFFER_DELIVERY_MODES.BATCH &&
            draft?.workupDeliveryMode !== OFFER_DELIVERY_MODES.INDIVIDUAL)
        }
      >
        <InputLabel>Workup Type</InputLabel>
        <Select
          value={draft?.workupReportType || 'fnmaSummary'}
          onChange={(e) => setDraft((prev) => ({ ...(prev || {}), workupReportType: e.target.value }))}
          label="Workup Type"
        >
          {workupTypesForUi.map((r) => (
            <MenuItem
              key={r.id}
              value={r.id}
              disabled={reportType === 'assessment' && r.id === 'fnmaSummary'}
            >
              {r.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
});

const normalizeTimeAdjustmentMonths = (value, legacyEnabled = false) => {
  if (value != null && value !== '') {
    const n = Math.floor(Number(value));
    if (Number.isFinite(n) && n >= 1) return n;
  }
  return legacyEnabled === true ? 3 : null;
};

export const DEFAULT_ADVANCED_SETTINGS = {
  autosave: true,
  manualReview: false,
  manualReviewOverride: 0,
  expandComps: false,
  defaultRunTypes: ['settled'],
  unreviewed_flag: 'False',
  compFilters: [],
  earliestSaleIntDate: 365,
  latestSaleIntDate: 365,
  squareFeetFilter: 40,
  grossAdjPctFilter: 30,
  netAdjPctFilter: 30,
  acreageFilter: 40,
  currentFilter: 'Default',
  squareFeetFilterEnabled: true,
  grossAdjPctFilterEnabled: true,
  netAdjPctFilterEnabled: true,
  acreageFilterEnabled: true,
  stage: 'SCAR',
  compLimit: null,
  highTaxRun: false,
  highTaxCount: 0,
  skipRerunOnInventoryUpdate: false,
  timeAdjustmentMonths: null,
  showSettledCourtdates: false,
  dateRange: [null, null],
  dateRangeType: 'preset',
  beforePreset: '12m',
  afterPreset: '12m',
};

export function mergeAdvancedSettings(overrides = {}, user) {
  const { useTimeAdjustedPrice: legacyUseTimeAdjustedPrice, ...rest } = overrides || {};
  const next = {
    ...DEFAULT_ADVANCED_SETTINGS,
    dateRange: [...DEFAULT_ADVANCED_SETTINGS.dateRange],
    compFilters: [...DEFAULT_ADVANCED_SETTINGS.compFilters],
    defaultRunTypes: [...DEFAULT_ADVANCED_SETTINGS.defaultRunTypes],
    showSettledCourtdates: user?.viewHistorical || DEFAULT_ADVANCED_SETTINGS.showSettledCourtdates,
    ...rest,
    unreviewed_flag:
      rest.unreviewed_flag === 'ManualOnly' || rest.unreviewed_flag === 'AutoAndManual'
        ? rest.unreviewed_flag
        : DEFAULT_ADVANCED_SETTINGS.unreviewed_flag,
    skipRerunOnInventoryUpdate: rest.skipRerunOnInventoryUpdate === true,
    timeAdjustmentMonths: normalizeTimeAdjustmentMonths(
      rest.timeAdjustmentMonths,
      legacyUseTimeAdjustedPrice === true
    ),
  };
  for (const key of Object.keys(DEFAULT_ADVANCED_SETTINGS)) {
    if (next[key] === undefined) next[key] = DEFAULT_ADVANCED_SETTINGS[key];
  }
  delete next.useTimeAdjustedPrice;
  return next;
}

export function loadAdvancedSettingsFromStorage(user) {
  try {
    const stored = localStorage.getItem('advancedSettings');
    if (!stored) return mergeAdvancedSettings({}, user);
    return mergeAdvancedSettings(JSON.parse(stored), user);
  } catch {
    return mergeAdvancedSettings({}, user);
  }
}

function AdvancedSettings({ open, onClose, onConfirm, initialSettings = {}, runAnalysis, filters, isLoadingFilters, fetchFilters, currentFilter, currentParcelId = null }) {
  const { canUseFeature, user } = useUser();
  const { reloadCourtDates } = useCourtDates();
  const showWeights = canUseFeature('update_weights');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState(currentFilter || '');
  const [filterName, setFilterName] = useState('');
  const [filterChanged, setFilterChanged] = useState(false);
  const [filterError, setFilterError] = useState('');
  const [isCreatingNewFilter, setIsCreatingNewFilter] = useState(false);
  const [saveAsAdmin, setSaveAsAdmin] = useState(false);
  const [isSavingFilter, setIsSavingFilter] = useState(false);
  const [originalSettings, setOriginalSettings] = useState(null); // Track original settings when filter is loaded
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);
  const settingsUpdated = useLocalDataStore((state) => state.settingsUpdated)
  const setSettingsUpdated = useLocalDataStore((state) => state.setSettingsUpdated)

  const [offerOptionsDraft, setOfferOptionsDraft] = useState(null);
  const offerOptionsSnapshotRef = useRef(null);

  useEffect(() => {
    if (open) {
      const snap = useOfferOptionsStore.getState();
      offerOptionsSnapshotRef.current = snap;
      setOfferOptionsDraft({
        includeCompsInOffersSpreadsheet: !!snap.includeCompsInOffersSpreadsheet,
        workupDeliveryMode: snap.workupDeliveryMode || OFFER_DELIVERY_MODES.OFFERS_ONLY,
        workupReportType: snap.workupReportType || 'fnmaSummary',
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const snap = offerOptionsSnapshotRef.current;
    if (!snap || !offerOptionsDraft) return;
    const changed =
      !!offerOptionsDraft.includeCompsInOffersSpreadsheet !== !!snap.includeCompsInOffersSpreadsheet ||
      (offerOptionsDraft.workupDeliveryMode || OFFER_DELIVERY_MODES.OFFERS_ONLY) !== (snap.workupDeliveryMode || OFFER_DELIVERY_MODES.OFFERS_ONLY) ||
      (offerOptionsDraft.workupReportType || 'fnmaSummary') !== (snap.workupReportType || 'fnmaSummary');
    if (changed && !settingsUpdated) setSettingsUpdated(true);
  }, [open, offerOptionsDraft, settingsUpdated, setSettingsUpdated]);

  const saveOfferOptions = () => {
    if (!offerOptionsDraft) return;
    useOfferOptionsStore.getState().setOfferOptions({
      includeCompsInOffersSpreadsheet: !!offerOptionsDraft.includeCompsInOffersSpreadsheet,
      workupDeliveryMode: offerOptionsDraft.workupDeliveryMode || OFFER_DELIVERY_MODES.OFFERS_ONLY,
      workupReportType: offerOptionsDraft.workupReportType || 'fnmaSummary',
    });
    offerOptionsSnapshotRef.current = useOfferOptionsStore.getState();
  };
  
  // Add local state for filters when not provided as props
  const [localFilters, setLocalFilters] = useState({ admin_filters: [], user_filters: [] });
  const [localIsLoadingFilters, setLocalIsLoadingFilters] = useState(false);
  
  // Initialize filters with default values if not provided
  const effectiveFilters = filters || localFilters;
  const effectiveIsLoadingFilters = isLoadingFilters || localIsLoadingFilters;
  
  // Add evaluation date state
  const [evalDate, setEvalDate] = useState(() => {
    // Default to July 1st of the year prior to current calendar year
    const currentYear = dayjs().year();
    return dayjs(`${currentYear - 1}-07-01`).format('YYYY-MM-DD');
  });
  const [isLoadingEvalDate, setIsLoadingEvalDate] = useState(false);

  // Local settings state, initialized from initialSettings with minimal required defaults
  const [settings, setSettings] = useState(() => mergeAdvancedSettings(initialSettings, user));

  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || wasOpen) return;

    setActiveTab(0);
    setSettingsUpdated(false);
    setFilterChanged(false);
    setFilterError('');
    setIsCreatingNewFilter(false);
    setSaveAsAdmin(false);
    setOriginalSettings(null);
    setShowOverrideWarning(false);
    setSettings(mergeAdvancedSettings(initialSettings, user));
  }, [open, initialSettings, user, setSettingsUpdated]);


  // Initialize selectedFilter when dialog opens with currentFilter prop or initialSettings
  useEffect(() => {
    if (open) {
      // Priority: currentFilter prop > initialSettings.currentFilter > empty string
      const filterToSet = currentFilter || initialSettings.currentFilter || '';
      setSelectedFilter(filterToSet);
      setFilterName(filterToSet);
      setIsCreatingNewFilter(filterToSet === 'new');
      
      // If we have a valid filter name, mark as not creating new filter
      if (filterToSet && filterToSet !== 'new') {     setIsCreatingNewFilter(false);
      }
    }
  }, [open, currentFilter, initialSettings.currentFilter]);

  // Initialize originalSettings when dialog opens with existing filter
  useEffect(() => {
    if (
      open &&
      selectedFilter &&
      selectedFilter !== 'new' &&
      ((effectiveFilters.admin_filters || []).length > 0 || (effectiveFilters.user_filters || []).length > 0)
    ) {
      // Find the selected filter in the filters array
      const allFilters = [
        ...(effectiveFilters.admin_filters || []),
        ...(effectiveFilters.user_filters || []),
      ];
      const selectedFilterData = allFilters.find(
        (f) => f.FilterName === selectedFilter
      );
      
      if (selectedFilterData) {
        // Use the utility function to process filter data with fetched evalDate
        const processedSettings = processFilterDataToSettings(
          selectedFilterData,
          evalDate
        );
        
        if (processedSettings) {
          const newSettings = {
            ...mergeAdvancedSettings(initialSettings, user),
            ...processedSettings,
          };
          setSettings(newSettings);
          setOriginalSettings(newSettings);
        }
      }
    }
    if (open && (!selectedFilter || selectedFilter === 'new')) {
      setOriginalSettings(null);
    }
  }, [
    open,
    selectedFilter,
    effectiveFilters.admin_filters,
    effectiveFilters.user_filters,
    evalDate,
    initialSettings,
    user,
  ]);

  // Function to fetch evaluation date from backend
  const fetchEvalDate = useCallback(async (muniCode, taxYear) => {
    setIsLoadingEvalDate(true);
    try {
      // update the extraction of eval date so this function
      const evalDate = await getEvaluationDate(muniCode, taxYear);
      setEvalDate(evalDate);
      return evalDate;
    } catch (error) {
      logger.error('Error fetching evaluation date:', error);
      // Default to July 1st of the year prior to tax year
      const fallbackDate = dayjs(`${taxYear - 1}-07-01`);
      setEvalDate(fallbackDate.format('YYYY-MM-DD'));
      return fallbackDate.format('YYYY-MM-DD');
    } finally {
      setIsLoadingEvalDate(false);
    }
  }, []);

  // Get MuniCode and TaxYear from URL parameters
  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      MuniCode: params.get('MuniCode'),
      TaxYear: params.get('TaxYear')
    };
  };
  
  const urlParams = getUrlParams();

  // Fetch evaluation date when dialog opens or location params change
  useEffect(() => {
    if (open && urlParams.TaxYear) {
      fetchEvalDate(urlParams.MuniCode, urlParams.TaxYear);
    }
  }, [open, urlParams.MuniCode, urlParams.TaxYear, fetchEvalDate]);

  // Local fetchFilters function when not provided as prop
  const localFetchFilters = useCallback(async () => {
    if (!user?.userID && !user?.id && !user?.userId) {
      logger.warn('No user ID available for fetching filters');
      return;
    }

    setLocalIsLoadingFilters(true);
    try {
      const response = await axiosInstance.post('/load-categorized-filters', {
        UserId: user?.userId
      });
      
      if (response.data && response.data.categorized_filters) {
        setLocalFilters(response.data.categorized_filters);
      }
    } catch (error) {
      logger.error('Error fetching filters:', error);
    } finally {
      setLocalIsLoadingFilters(false);
    }
  }, [user]);

  // Load filters when dialog opens and filters are not provided as props
  useEffect(() => {
    if (open && !filters && !fetchFilters) {
      localFetchFilters();
    }
  }, [open, filters, fetchFilters, localFetchFilters]);

  // Use the provided fetchFilters or local one
  const effectiveFetchFilters = fetchFilters || localFetchFilters;
  
  // Handle filter selection and update local settings
  const handleFilterSelection = (newValue) => {
    if (newValue === 'new') {
      setIsCreatingNewFilter(true);
      setSelectedFilter(newValue);
      setFilterChanged(false);
      setSaveAsAdmin(false);
      setOriginalSettings(null);
    } else {
      setIsCreatingNewFilter(false);
      setSelectedFilter(newValue);
      // Mark as changed when switching to a different filter so user can save
      setFilterChanged(true);
      setSettingsUpdated(true);
      
      // Find the selected filter in the filters array
      const allFilters = [...(effectiveFilters.admin_filters || []), ...(effectiveFilters.user_filters || [])];
      const selectedFilterData = allFilters.find(f => f.FilterName === newValue);
      
      if (selectedFilterData) {
        // Set the filter name for editing
        setFilterName(newValue);
        
        // Check if this is an admin filter (UserID is null)
        const isAdminFilter = selectedFilterData.UserID === null;
        setSaveAsAdmin(isAdminFilter);
        
        // Use the utility function to process filter data with fetched evalDate
        const processedSettings = processFilterDataToSettings(selectedFilterData, evalDate);
        
        if (processedSettings) {
          const newSettings = {
            ...mergeAdvancedSettings(initialSettings, user),
            ...processedSettings,
          };
          setSettings(newSettings);
          setOriginalSettings(newSettings);
        }
      }
    }
  };

  // Set current filter in localStorage
  const setCurrentFilter = (filterName) => {
    const storedSettings = localStorage.getItem('advancedSettings');
    if (storedSettings) {
      const parsed = JSON.parse(storedSettings);
      parsed.currentFilter = filterName;
      localStorage.setItem('advancedSettings', JSON.stringify(parsed));
    }
  };

 useEffect(() => {
    const storedSettings = localStorage.getItem('advancedSettings');
    if (!storedSettings) {
      localStorage.setItem('advancedSettings', JSON.stringify({
        currentFilter: null,
        settings: {}
      }));
    }
  }, []);

  // Set default filter name when component mounts or when selectedFilter changes to 'new'
  useEffect(() => {
    if (selectedFilter === 'new' && isCreatingNewFilter && !filterName) {
      // Don't auto-set any default name, leave it blank for user to enter
    }
  }, [selectedFilter, isCreatingNewFilter, filterName]);

  // const evalDate = getEvalDate();

  // Calculate dates based on presets
  const calculatePresetDates = (beforePreset, afterPreset) => {
    // Use the fetched evaluation date
    const evalDateObj = dayjs(evalDate);
    if (!evalDateObj.isValid()) {
      logger.error('Invalid evaluation date:', evalDate);
      return [null, null];
    }
    
    // Handle "nocutoff" values - return null for that side
    const effectiveBeforePreset = beforePreset === 'nocutoff' ? null : beforePreset;
    const effectiveAfterPreset = afterPreset === 'nolimit' ? null : afterPreset;
    
    // Use the unified date range calculation
    const result = calculateUnifiedDateRange(
      evalDateObj.format('YYYY-MM-DD'),
      effectiveBeforePreset,
      effectiveAfterPreset
    );
    
    return result;
  };  

  // Check if user is admin or propAdmin
  const isAdminType = user?.userAccess?.level === 'admin' || user?.userAccess?.level === 'propadmin';
  const isPropAdmin = user?.userAccess?.level === 'propadmin';
  
  // Helper function to determine if delete button should be shown
  const canDeleteFilter = (filterName) => {
    if (!filterName || filterName === 'new') {
      return false;
    }

    // Prevent deletion of any filter named "Default"
    if (filterName.toLowerCase() === 'default') {
      return false;
    }

    // Check if this is an admin filter
    const allFilters = [...(effectiveFilters.admin_filters || []), ...(effectiveFilters.user_filters || [])];
    const selectedFilterData = allFilters.find(f => f.FilterName === filterName);
    
    if (selectedFilterData) {
      // Default filters (UserID is null) can only be deleted by a Propriety admin
      if (selectedFilterData.UserID === null && !isPropAdmin) {
        return false;
      }
    }

    return true;
  };

  // Save filter function
  const handleSaveFilter = async (isAdminFilter = false) => {
    if (!filterName.trim()) {
      logger.error('Filter name is required');
      toast.error('Filter name is required');
      return;
    }

    const updatingDefault = selectedFilter !== 'new' && (effectiveFilters.admin_filters || []).some((f) => f.FilterName === selectedFilter);
    if ((isAdminFilter || updatingDefault) && !isPropAdmin) {
      toast.error('Only Propriety admins can edit default filters.');
      return false;
    }

    // Clear any previous errors
    setFilterError('');
    
    // Set loading state
    setIsSavingFilter(true);

    // Check if we should apply filters based on date range type
    let shouldApplyBeforeFilter = false;
    let shouldApplyAfterFilter = false;
    
    if (settings.dateRangeType === 'preset') {
      // For preset mode, use preset values to determine if filters should be applied
      shouldApplyBeforeFilter = settings.beforePreset && settings.beforePreset !== 'nocutoff';
      shouldApplyAfterFilter = settings.afterPreset && settings.afterPreset !== 'nolimit';
    } else {
      // For custom mode, apply filters if custom dates are provided
      shouldApplyBeforeFilter = settings.dateRange && settings.dateRange[0];
      shouldApplyAfterFilter = settings.dateRange && settings.dateRange[1];
    }

    // Calculate integer date values for earliest and latest sale dates
    let earliestSaleIntDate = null;
    let latestSaleIntDate = null;
    
    if (shouldApplyBeforeFilter && settings.dateRange && settings.dateRange[0] && evalDate) {
      earliestSaleIntDate = Math.abs(dayjs(evalDate).diff(dayjs(settings.dateRange[0]), 'day'));
    }
    
    if (shouldApplyAfterFilter && settings.dateRange && settings.dateRange[1] && evalDate) {
      latestSaleIntDate = Math.abs(dayjs(settings.dateRange[1]).diff(dayjs(evalDate), 'day'));
    }

    // Prepare settings with toggle-aware filter values and proper date formatting
    const settingsToSave = {
      ...settings,
      // Set filter values to null if disabled, otherwise use the current value
      squareFeetFilter: settings.squareFeetFilterEnabled ? settings.squareFeetFilter : null,
      acreageFilter: settings.acreageFilterEnabled ? settings.acreageFilter : null,
      grossAdjPctFilter: settings.grossAdjPctFilterEnabled ? settings.grossAdjPctFilter : null,
      netAdjPctFilter: settings.netAdjPctFilterEnabled ? settings.netAdjPctFilter : null,
      evalDate: evalDate, // Include the fetched evaluation date
      // Only save integer date values - let initialization fetch handle date calculations
      earliestSaleIntDate: earliestSaleIntDate,
      latestSaleIntDate: latestSaleIntDate,
      // Don't save date strings - they will be calculated from integer values during fetch
      earliestSaleDate: null,
      latestSaleDate: null
    };

    const filterParams = {
      name: selectedFilter === 'new' ? filterName : selectedFilter,
      muniCode: urlParams.MuniCode || 'All',
      taxYear: urlParams.TaxYear,
      isAdminFilter,
              UserId: isAdminFilter ? null : user?.userId, // Only use user.userId
      settings: settingsToSave
    };

    try {
      const response = await axiosInstance.post('/save-filter', filterParams);
      
      if (response.data.success) {
        
        // Clear any existing errors on successful save
        setFilterError('');
        
        // Only update localStorage and trigger other actions on successful save
        const updatedGlobalSettings = {
          ...initialSettings,
          ...settings,
          currentFilter: selectedFilter === 'new' ? filterName : selectedFilter
        };
        localStorage.setItem('advancedSettings', JSON.stringify(updatedGlobalSettings));
        mergeNegotiationObjWithAdvancedSettings(updatedGlobalSettings);

        // Trigger settings update to notify CaseReview
        if (onConfirm) {
          onConfirm(updatedGlobalSettings);
        }
        
        // Set settingsUpdated flag to trigger CaseReview re-read
        setSettingsUpdated(true);
        
        setFilterChanged(false); // Reset filter changed state after successful save
        
        // Refresh filters after saving
        await effectiveFetchFilters();
        
        // Set the newly created/updated filter as selected
        const savedFilterName = selectedFilter === 'new' ? filterName : selectedFilter;
        setSelectedFilter(savedFilterName);
        setCurrentFilter(savedFilterName);
        setIsCreatingNewFilter(false);
        
        // If this was a new filter, reset the filter name field
        if (selectedFilter === 'new') {
          setFilterName('');
        }

        toast.success('Filter saved successfully!');
        return true; // Indicate success
      } else {
        // Handle error from backend
        const errorMessage = response.data.error || 'Failed to save filter';
        setFilterError(errorMessage);
        return false; // Indicate failure
      }
    } catch (error) {
      logger.error('Error saving filter:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save filter. Please try again.';
      setFilterError(errorMessage);
      return false; // Indicate failure
    } finally {
      // Clear loading state
      setIsSavingFilter(false);
    }
  };

  // Delete filter function
  const handleDeleteFilter = async () => {
    if (!selectedFilter || selectedFilter === 'new') {
      return;
    }

    // Prevent deletion of any filter named "Default"
    if (selectedFilter.toLowerCase() === 'default') {
      toast.error('Cannot delete the Default filter.');
      return;
    }

    // Check if this is an admin filter
    const allFilters = [...(effectiveFilters.admin_filters || []), ...(effectiveFilters.user_filters || [])];
    const selectedFilterData = allFilters.find(f => f.FilterName === selectedFilter);
    
    if (selectedFilterData) {
      if (selectedFilterData.UserID === null && !isPropAdmin) {
        toast.error('Only Propriety admins can delete default filters.');
        return;
      }
    }

    try {
      const response = await axiosInstance.post('/delete-filter', {
        filterName: selectedFilter,
        // Only send userId if user is not admin (admin users should send null to bypass user restriction)
        UserId: isAdminType ? null : user?.userId
      });
      
      if (response.data.success) {
        // Refresh filters after deleting
        await effectiveFetchFilters();
        // Reset to new filter
        setSelectedFilter('new');
        setCurrentFilter(null);
        setFilterChanged(false);
        setIsCreatingNewFilter(true);
        // Don't clear the filter name - let user keep what they were typing

        toast.success('Filter deleted successfully!');
      } else {
        logger.error('Error deleting filter:', response.data.error);
        toast.error(response.data.error || 'Failed to delete filter');
      }
    } catch (error) {
      logger.error('Error deleting filter:', error);
    }
  };



  // Initialize date range with preset values if not already set
  // useEffect(() => {
  //   logger.log('useEffect: Initialize date range - settings.dateRangeType:', settings.dateRangeType, 'settings.dateRange:', settings.dateRange);
  //   if (settings.dateRangeType === 'preset' && (!settings.dateRange[0] || !settings.dateRange[1])) {
  //     const presetDates = calculatePresetDates(settings.beforePreset, settings.afterPreset);
  //     logger.log('useEffect: Calculating preset dates:', presetDates);
  //     setSettings(prev => ({
  //       ...prev,
  //       dateRange: presetDates
  //     }));
  //   }
  // }, [evalDate, settings.dateRangeType, settings.beforePreset, settings.afterPreset]); // Run when evalDate or preset values change

  // Recalculate date range when evaluation date or presets change (for preset mode)
  // useEffect(() => {
  //   logger.log('useEffect: Recalculate date range - settings.dateRangeType:', settings.dateRangeType, 'evalDate:', evalDate);
  //   if (settings.dateRangeType === 'preset' && evalDate) {
  //     const presetDates = calculatePresetDates(settings.beforePreset, settings.afterPreset);
  //     logger.log('useEffect: Recalculating preset dates:', presetDates);
  //     
  //     // Only update if the dateRange is different from what it should be
  //     if (
  //       settings.dateRange[0] !== presetDates[0] ||
  //       settings.dateRange[1] !== presetDates[1]
  //     ) {
  //       logger.log('useEffect: Updating dateRange from', settings.dateRange, 'to', presetDates);
  //       setSettings(prev => ({
  //         ...prev,
  //         dateRange: presetDates
  //       }));
  //       if (!settingsUpdated) {
  //         setSettingsUpdated(true);
  //       }
  //     }
  //   }
  //   // If custom, do nothing
  // }, [evalDate, settings.beforePreset, settings.afterPreset, settings.dateRangeType]);
  
  // Calculate preset dates once evalDate is available (if needed)
  // useEffect(() => {
  //   if (evalDate && settings.dateRangeType === 'preset' && (!settings.dateRange[0] || !settings.dateRange[1])) {
  //     const presetDates = calculatePresetDates(settings.beforePreset, settings.afterPreset);
  //     if (presetDates[0] && presetDates[1]) {
  //       setSettings(prev => ({
  //         ...prev,
  //         dateRange: presetDates
  //       }));
  //     }
  //   }
  // }, [evalDate, settings.dateRangeType, settings.beforePreset, settings.afterPreset]);

  // Recalculate date range once evalDate becomes available (if we have filter data)
  useEffect(() => {
    if (evalDate && selectedFilter && selectedFilter !== 'new' && 
        (settings.earliestSaleDate !== null || settings.latestSaleDate !== null)) {
      // We have evalDate and filter data, recalculate the date range
      const dateSettings = decodeFilterDateSettings(
        settings.earliestSaleDate,
        settings.latestSaleDate,
        evalDate
      );
      
      // Only update if the date range type or dates have changed
      if (dateSettings.dateRangeType !== settings.dateRangeType ||
          dateSettings.beforePreset !== settings.beforePreset ||
          dateSettings.afterPreset !== settings.afterPreset ||
          JSON.stringify(dateSettings.dateRange) !== JSON.stringify(settings.dateRange)) {
        
        setSettings(prev => ({
          ...prev,
          dateRangeType: dateSettings.dateRangeType,
          beforePreset: dateSettings.beforePreset,
          afterPreset: dateSettings.afterPreset,
          dateRange: dateSettings.dateRange
        }));
      }
    }
  }, [evalDate, selectedFilter, settings.earliestSaleDate, settings.latestSaleDate]);

  // Add refs for date inputs
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  
  // Add state for calendar open/close
  const [startCalendarOpen, setStartCalendarOpen] = useState(false);
  
  // const [weightsOpen, setWeightsOpen] = useState(false);

  // const [settingsUpdated, setSettingsUpdated] = useState(false);
  // const getAdjustments = useCompStore((state) => state.adjustments);
  
  function normalizeDate(date) {
    // If the date is null or undefined, return null
    if (!date) return null;
    
    // Convert to a Day.js object and validate
    const normalized = dayjs(date);
    return normalized.isValid() ? normalized : null;
  }

  const handleTabChange = (e, newValue) => {
    setActiveTab(newValue);
    // if (newValue === 3) {
    //   setWeightsOpen(true);
    // }
  };

  const handleClose = () => {
    if(settingsUpdated){setSettingsUpdated(false)}
    onClose();

  };

  const handleSetTab = (tabIndex) => setActiveTab(tabIndex);

  const handleFieldChange = (name, value) => {
    if (name === 'dateRange') {
      // Validate dates before updating state
      const [startDate, endDate] = value;
      const normalizedStartDate = startDate ? dayjs(startDate).isValid() ? startDate : null : null;
      const normalizedEndDate = endDate ? dayjs(endDate).isValid() ? endDate : null : null;
      
      setSettings(prev => ({
        ...prev,
        dateRange: [normalizedStartDate, normalizedEndDate]
      }));
    } else if (name === 'dateRangeType') {
      setSettings(prev => ({ 
        ...prev, 
        dateRangeType: value,
        // If switching to preset, calculate dates based on current presets
        // If switching to custom, clear the date range
        dateRange: value === 'preset' 
          ? calculatePresetDates(prev.beforePreset, prev.afterPreset)
          : [null, null]
      }));
    } else if (name === 'beforePreset' || name === 'afterPreset') {
      setSettings(prev => {
        const newSettings = { ...prev, [name]: value };
        // If using preset type, update the date range
        if (newSettings.dateRangeType === 'preset') {
          const beforePreset = name === 'beforePreset' ? value : newSettings.beforePreset;
          const afterPreset = name === 'afterPreset' ? value : newSettings.afterPreset;
          newSettings.dateRange = calculatePresetDates(beforePreset, afterPreset);
        }
        return newSettings;
      });
    } else {
      setSettings(prev => ({ ...prev, [name]: value }));
    }
    
    // Track filter changes
    if (!filterChanged) {
      setFilterChanged(true);
    }
    
    if (!settingsUpdated) {
      setSettingsUpdated(true);
    }
  };

  const savePage = () => {
    // Check if showSettledCourtdates changed
    const previousShowSettledCourtdates = initialSettings.showSettledCourtdates || false;
    const currentShowSettledCourtdates = settings.showSettledCourtdates || false;
    const showSettledCourtdatesChanged = previousShowSettledCourtdates !== currentShowSettledCourtdates;

    // Check if we should apply filters based on date range type
    let shouldApplyBeforeFilter = false;
    let shouldApplyAfterFilter = false;
    
    if (settings.dateRangeType === 'preset') {
      // For preset mode, use preset values to determine if filters should be applied
      shouldApplyBeforeFilter = settings.beforePreset && settings.beforePreset !== 'nocutoff';
      shouldApplyAfterFilter = settings.afterPreset && settings.afterPreset !== 'nolimit';
    } else {
      // For custom mode, apply filters if custom dates are provided
      shouldApplyBeforeFilter = settings.dateRange && settings.dateRange[0];
      shouldApplyAfterFilter = settings.dateRange && settings.dateRange[1];
    }

    // Calculate integer date values for earliest and latest sale dates
    let earliestSaleIntDate = null;
    let latestSaleIntDate = null;
    
    if (shouldApplyBeforeFilter && settings.dateRange && settings.dateRange[0] && evalDate) {
      earliestSaleIntDate = Math.abs(dayjs(evalDate).diff(dayjs(settings.dateRange[0]), 'day'));
    }
    
    if (shouldApplyAfterFilter && settings.dateRange && settings.dateRange[1] && evalDate) {
      latestSaleIntDate = Math.abs(dayjs(settings.dateRange[1]).diff(dayjs(evalDate), 'day'));
    }

    // Apply the current settings to the global store
    const updatedGlobalSettings = {
      ...initialSettings,
      ...settings,
      currentFilter: selectedFilter, // Ensure currentFilter is up to date
      evalDate: evalDate, // Include the fetched evaluation date
      // Only save integer date values - let initialization fetch handle date calculations
      earliestSaleIntDate: earliestSaleIntDate,
      latestSaleIntDate: latestSaleIntDate,
      // Don't save date strings - they will be calculated from integer values during fetch
      earliestSaleDate: null,
      latestSaleDate: null,
      // Preserve null for compLimit if not explicitly set (dynamic default: 20 assessment, 10 others)
      compLimit: settings.compLimit === '' || settings.compLimit === undefined || settings.compLimit === null ? null : settings.compLimit,
      timeAdjustmentMonths: normalizeTimeAdjustmentMonths(settings.timeAdjustmentMonths),
    };
    delete updatedGlobalSettings.useTimeAdjustedPrice;
    
    // Save to localStorage
    localStorage.setItem('advancedSettings', JSON.stringify(updatedGlobalSettings));
    mergeNegotiationObjWithAdvancedSettings(updatedGlobalSettings);

    // Trigger court dates reload if showSettledCourtdates changed
    if (showSettledCourtdatesChanged && reloadCourtDates) {
      reloadCourtDates();
    }
    
    // Trigger settings update
    if (onConfirm) {
      onConfirm(updatedGlobalSettings);
    }

  }

  const renderSalesAnalysisTab = () => (
    <>
      <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
        <Typography variant="h6" sx={{ mb: 0 }}>General Settings</Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          The selections you make will be saved as your default options.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 4 },
            alignItems: 'flex-start',
            maxWidth: '100%',
          }}
        >
          <Box
            sx={{
              display: 'inline-grid',
              width: 'max-content',
              maxWidth: '100%',
              gridTemplateColumns: 'max-content 120px',
              columnGap: 1.5,
              rowGap: 0,
              alignItems: 'start',
            }}
          >
            <FormControlLabel
              className="items-center"
              control={
                <Checkbox
                  checked={settings.autosave}
                  onChange={(e) => {
                    const autosaveValue = e.target.checked;
                    handleFieldChange('autosave', autosaveValue);
                    handleFieldChange('manualReview', !autosaveValue);
                    if (!autosaveValue) {
                      if (settings.manualReviewOverride === 1) handleFieldChange('manualReviewOverride', 0);
                      if (settings.expandComps) handleFieldChange('expandComps', false);
                    }
                  }}
                  sx={{ pointerEvents: 'auto' }}
                />
              }
              label="Autosave"
              sx={{
                alignItems: 'center',
                pointerEvents: 'none',
                mb: 0,
                '& .MuiCheckbox-root': { pointerEvents: 'auto' },
                '& .MuiFormControlLabel-label': { pointerEvents: 'auto' },
              }}
            />
            <Box
              sx={{
                gridRow: '1 / 4',
                gridColumn: 2,
                width: 120,
                alignSelf: 'center',
                visibility: (settings.autosave || settings.expandComps) ? 'visible' : 'hidden',
                pointerEvents: (settings.autosave || settings.expandComps) ? 'auto' : 'none',
              }}
              aria-hidden={!(settings.autosave || settings.expandComps)}
            >
              <FormControl sx={{ width: 120 }} size="small">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.25 }}>
                  <FormLabel sx={{ fontSize: '0.875rem' }}># Comps</FormLabel>
                  <Tooltip title="Sets how many comps will be automatically saved when autosave is enabled. If not set, defaults to 20 for assessment and 10 for other run types." placement="top">
                    <IconButton size="small" sx={{ padding: '2px' }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  type="number"
                  value={settings.compLimit !== undefined && settings.compLimit !== null && settings.compLimit !== '' ? settings.compLimit : undefined}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (inputValue === '') return handleFieldChange('compLimit', null);
                    const value = parseInt(inputValue);
                    if (!isNaN(value) && value >= 1 && value <= 50) handleFieldChange('compLimit', value);
                  }}
                  placeholder="Auto"
                  size="small"
                  tabIndex={(settings.autosave || settings.expandComps) ? 0 : -1}
                  sx={{ width: 120, '& .MuiInputBase-root': { height: 36 } }}
                />
              </FormControl>
            </Box>

            <FormHelperText
              aria-hidden={!settings.autosave}
              sx={{
                color: settings.autosave ? 'green' : 'transparent',
                visibility: settings.autosave ? 'visible' : 'hidden',
                mt: -0.75,
                ml: 3.25,
                lineHeight: 1.15,
                minHeight: '1.15em',
              }}
            >
              {settings.autosave ? '*Comps will automatically save after run' : '\u00A0'}
            </FormHelperText>

            <FormControlLabel
              className="items-center"
              disabled={!settings.autosave}
              control={
                <Checkbox
                  checked={settings.expandComps || false}
                  onChange={(e) => {
                    const v = e.target.checked;
                    handleFieldChange('expandComps', v);
                    if (v && settings.manualReviewOverride === 1) handleFieldChange('manualReviewOverride', 0);
                  }}
                  sx={{ pointerEvents: 'auto' }}
                />
              }
              label={
                <Typography sx={{ color: settings.autosave ? undefined : 'text.disabled', fontWeight: 500 }}>
                  Expand Comps
                </Typography>
              }
              sx={{
                alignItems: 'center',
                pointerEvents: 'none',
                '& .MuiCheckbox-root': { pointerEvents: 'auto' },
                '& .MuiFormControlLabel-label': { pointerEvents: 'auto' },
              }}
            />

            <FormControlLabel
              className="items-center"
              disabled={!settings.autosave || settings.expandComps}
              control={
                <Checkbox
                  checked={settings.manualReviewOverride === 1}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setShowOverrideWarning(true);
                    } else {
                      handleFieldChange('manualReviewOverride', 0);
                    }
                  }}
                  sx={{
                    pointerEvents: 'auto',
                    color: settings.autosave && !settings.expandComps ? 'red' : 'action.disabled',
                    '&.Mui-checked': {
                      color: settings.autosave && !settings.expandComps ? 'red' : 'action.disabled',
                    },
                  }}
                />
              }
              label={
                <Tooltip title="Override manually reviewed comps" placement="top">
                  <Typography
                    noWrap
                    sx={{
                      maxWidth: { xs: '44ch', sm: '60ch' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: settings.autosave && !settings.expandComps ? 'red' : 'text.disabled',
                      fontWeight: 500,
                    }}
                  >
                    Override manually reviewed comps
                  </Typography>
                </Tooltip>
              }
              sx={{
                width: 'max-content',
                maxWidth: '100%',
                alignItems: 'center',
                pointerEvents: 'none',
                '& .MuiCheckbox-root': { pointerEvents: 'auto' },
                '& .MuiFormControlLabel-label': { pointerEvents: 'auto' },
              }}
            />
            <Box sx={{ width: 120 }} />
          </Box>

          <Box
            sx={{
              display: 'inline-grid',
              width: 'max-content',
              maxWidth: '100%',
              gridTemplateColumns: 'max-content 120px',
              columnGap: 1.5,
              rowGap: 0,
              alignItems: 'start',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', width: 'max-content', maxWidth: '100%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!settings.highTaxRun}
                    onChange={(e) => {
                      const on = e.target.checked;
                      handleFieldChange('highTaxRun', on);
                      if (on && (!Number.isFinite(Number(settings.highTaxCount)) || Number(settings.highTaxCount) < 1)) {
                        handleFieldChange('highTaxCount', 25);
                      }
                      if (!on) handleFieldChange('highTaxCount', 0);
                    }}
                    sx={{ pointerEvents: 'auto' }}
                  />
                }
                label="High tax run"
                sx={{
                  alignItems: 'center',
                  pointerEvents: 'none',
                  mb: 0,
                  '& .MuiCheckbox-root': { pointerEvents: 'auto' },
                  '& .MuiFormControlLabel-label': { pointerEvents: 'auto' },
                }}
              />
              <FormHelperText sx={{ mt: -0.75, ml: 3.25, lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                Return X highest tax cases.
              </FormHelperText>
            </Box>

            <Box
              sx={{
                gridRow: '1 / 2',
                gridColumn: 2,
                width: 120,
                alignSelf: 'center',
                visibility: settings.highTaxRun ? 'visible' : 'hidden',
                pointerEvents: settings.highTaxRun ? 'auto' : 'none',
              }}
              aria-hidden={!settings.highTaxRun}
            >
              <FormControl sx={{ width: 120 }} size="small">
                <FormLabel sx={{ fontSize: '0.875rem', mb: 0.25, display: 'block' }}>High tax count</FormLabel>
                <TextField
                  type="number"
                  value={settings.highTaxCount ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') return handleFieldChange('highTaxCount', '');
                    const n = Number(v);
                    if (Number.isFinite(n) && n >= 1) handleFieldChange('highTaxCount', Math.floor(n));
                  }}
                  inputProps={{ min: 1 }}
                  size="small"
                  tabIndex={settings.highTaxRun ? 0 : -1}
                  sx={{ width: 120, '& .MuiInputBase-root': { height: 36 } }}
                />
              </FormControl>
            </Box>

            <FormControlLabel
              className="items-center"
              control={
                <Checkbox
                  checked={settings.showSettledCourtdates || false}
                  onChange={(e) => handleFieldChange('showSettledCourtdates', e.target.checked)}
                  sx={{ pointerEvents: 'auto' }}
                />
              }
              label="Show Settled Courtdates"
              sx={{
                alignItems: 'center',
                pointerEvents: 'none',
                '& .MuiCheckbox-root': { pointerEvents: 'auto' },
                '& .MuiFormControlLabel-label': { pointerEvents: 'auto' },
              }}
            />
            <Box sx={{ width: 120 }} />

            <FormControlLabel
              className="items-center"
              control={
                <Checkbox
                  checked={!!settings.skipRerunOnInventoryUpdate}
                  onChange={(e) => handleFieldChange('skipRerunOnInventoryUpdate', e.target.checked)}
                  sx={{ pointerEvents: 'auto' }}
                />
              }
              label={
                <Tooltip title="Save inventory changes to the database without re-running comparable analysis." placement="top">
                  <Typography sx={{ fontWeight: 500 }}>Do not Re-run on inventory updates</Typography>
                </Tooltip>
              }
              sx={{
                alignItems: 'center',
                pointerEvents: 'none',
                '& .MuiCheckbox-root': { pointerEvents: 'auto' },
                '& .MuiFormControlLabel-label': { pointerEvents: 'auto' },
              }}
            />
            <Box sx={{ width: 120 }} />

            <FormControlLabel
              className="items-center"
              disabled
              control={
                <Checkbox
                  checked={settings.timeAdjustmentMonths != null && Number(settings.timeAdjustmentMonths) >= 1}
                  onChange={(e) => {
                    handleFieldChange('timeAdjustmentMonths', e.target.checked ? 3 : null);
                  }}
                  disabled
                  sx={{ pointerEvents: 'auto' }}
                />
              }
              label="Use Time Adjusted Price"
              sx={{
                alignItems: 'center',
                pointerEvents: 'none',
                '& .MuiCheckbox-root': { pointerEvents: 'auto' },
                '& .MuiFormControlLabel-label': { pointerEvents: 'auto' },
              }}
            />
            <Box
              sx={{
                width: 120,
                alignSelf: 'center',
                position: 'relative',
                visibility: settings.timeAdjustmentMonths != null ? 'visible' : 'hidden',
                pointerEvents: settings.timeAdjustmentMonths != null ? 'auto' : 'none',
              }}
              aria-hidden={settings.timeAdjustmentMonths == null}
            >
              <FormLabel
                sx={{
                  position: 'absolute',
                  left: 0,
                  bottom: '100%',
                  mb: 0.25,
                  fontSize: '0.875rem',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                }}
              >
                # Months
              </FormLabel>
              <TextField
                type="number"
                value={settings.timeAdjustmentMonths ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') return handleFieldChange('timeAdjustmentMonths', '');
                  if (!/^\d+$/.test(v)) return;
                  const n = parseInt(v, 10);
                  if (n >= 1) handleFieldChange('timeAdjustmentMonths', n);
                }}
                onBlur={() => {
                  if (settings.timeAdjustmentMonths == null) return;
                  const n = Math.floor(Number(settings.timeAdjustmentMonths));
                  if (!Number.isFinite(n) || n < 1) handleFieldChange('timeAdjustmentMonths', 3);
                }}
                inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                size="small"
                tabIndex={settings.timeAdjustmentMonths != null ? 0 : -1}
                sx={{ width: 120, '& .MuiInputBase-root': { height: 36 } }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          gap: 2,
          alignItems: { xs: 'flex-start', sm: 'center' },
          maxWidth: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexShrink: 0, minWidth: 0, maxWidth: '100%' }}>
          {/* Comp Filter Selection */}
          <FormControl sx={{ width: '100%', maxWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
              <FormLabel sx={{ fontSize: '0.875rem' }}>Comp Filter being used:</FormLabel>
              <Tooltip title="This will determine the comps displayed in the court tab - typically this should be left as Default" placement="top">
                <IconButton size="small" sx={{ padding: '0px' }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Select
              value={effectiveIsLoadingFilters ? '' : selectedFilter}
              onChange={(e) => handleFilterSelection(e.target.value)}
              size="small"
              sx={{
                height: '40px',
                fontSize: '0.875rem'
              }}
              disabled={effectiveIsLoadingFilters}
              displayEmpty
              renderValue={(value) => {
                if (effectiveIsLoadingFilters) {
                  return <span style={{ color: '#666' }}>Loading...</span>;
                }
                return value || 'Select a filter';
              }}
            >
              <MenuItem value="new">Create New Filter</MenuItem>

              {/* Default Filters */}
              {effectiveFilters.admin_filters.length > 0 && (
                <ListSubheader>Default Filters</ListSubheader>
              )}
              {(effectiveFilters.admin_filters || []).map((filter) => (
                <MenuItem key={filter.FilterName} value={filter.FilterName}>
                  {filter.FilterName}
                </MenuItem>
              ))}

              {/* User Filters */}
              {(effectiveFilters.user_filters || []).length > 0 && (
                <ListSubheader>User Filters</ListSubheader>
              )}
              {(effectiveFilters.user_filters || []).map((filter) => (
                <MenuItem key={filter.FilterName} value={filter.FilterName}>
                  {filter.FilterName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Algorithm Stage Run Type Selection */}
          <FormControl sx={{ width: '100%', maxWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <FormLabel sx={{ fontSize: '0.875rem' }}>Algorithm Stage Run Type:</FormLabel>
              <Tooltip title="This determines how aggressively comps will be selected, and what LOA to use if relevant">
                <IconButton size="small" sx={{ padding: '2px' }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Select
              value={settings.stage}
              onChange={(e) => handleFieldChange('stage', e.target.value)}
              size="small"
              sx={{
                height: '32px',
                fontSize: '0.875rem'
              }}
            >
              <MenuItem value="BAR">BAR (or ARC)</MenuItem>
              <MenuItem value="SCAR">SCAR</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Subject filtering selection */}
        <FormControl component="fieldset" sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
          <FormLabel className=''>Subject Filtering:</FormLabel>
          <Box
            sx={{
              mt: 0.25,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              columnGap: 0.5,
              rowGap: 0.5,
              maxWidth: '100%',
            }}
          >
            {[
              { value: 'scarFiled', label: 'Show SCAR Filed Only', helper: 'Only show cases where SCAR has been filed.' },
              { value: 'settled', label: 'Show Settled Cases', helper: 'Include cases that have already settled.' },
            ].map((option) => {
              const checked = settings.defaultRunTypes?.includes(option.value) || false;
              return (
                <Box key={option.value} sx={{ display: 'flex', flexDirection: 'column', width: 'max-content', flexShrink: 0 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          const currentValues = settings.defaultRunTypes || [];
                          const newValues = e.target.checked
                            ? [...currentValues, option.value]
                            : currentValues.filter((val) => val !== option.value);
                          handleFieldChange('defaultRunTypes', newValues);
                        }}
                      />
                    }
                    label={option.label}
                    sx={{
                      mb: 0,
                      ml: 0,
                      '& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' },
                    }}
                  />
                  <FormHelperText sx={{ mt: -0.75, ml: 3.25, lineHeight: 1.15 }}>
                    {option.helper}
                  </FormHelperText>
                </Box>
              );
            })}
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={settings.unreviewed_flag !== 'False'}
                      onChange={(e) =>
                        handleFieldChange('unreviewed_flag', e.target.checked ? 'ManualOnly' : 'False')
                      }
                    />
                  }
                  label="Show Unreviewed Only"
                  sx={{ mb: 0, ml: 0 }}
                />
                <Box
                  sx={{
                    minHeight: 36,
                    width: 260,
                    maxWidth: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    visibility: settings.unreviewed_flag !== 'False' ? 'visible' : 'hidden',
                    pointerEvents: settings.unreviewed_flag !== 'False' ? 'auto' : 'none',
                  }}
                  aria-hidden={settings.unreviewed_flag === 'False'}
                >
                  <FormControl size="small" sx={{ width: 260, maxWidth: '100%' }}>
                    <Select
                      value={settings.unreviewed_flag === 'False' ? 'ManualOnly' : settings.unreviewed_flag}
                      onChange={(e) => handleFieldChange('unreviewed_flag', e.target.value)}
                      tabIndex={settings.unreviewed_flag !== 'False' ? 0 : -1}
                      sx={{
                        '& .MuiInputBase-root': { height: 36 },
                        '& .MuiSelect-select': {
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          py: 0,
                        },
                      }}
                    >
                      <MenuItem value="ManualOnly">No Manual Review</MenuItem>
                      <MenuItem value="AutoAndManual">No Review at all</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <FormHelperText sx={{ mt: -0.75, ml: 3.25, lineHeight: 1.15 }}>
                Hide cases that have been reviewed.
              </FormHelperText>
            </Box>
          </Box>
        </FormControl>
      </Box>

      {/* Simple Save button for General tab */}
      <div className="absolute right-2 bottom-2 justify-end">
        <Button onClick={handleClose}>Close</Button>
        <Button
          onClick={() => {
            if (settingsUpdated) { setSettingsUpdated(false); }
            savePage();
            // Show success toast for filter selection changes
            if (filterChanged) {
              toast.success('User settings saved successfully!');
              setFilterChanged(false);
            }
            handleClose();
          }}
          variant="contained"
          disabled={!settingsUpdated && !filterChanged}
        >
          Save
        </Button>
      </div>
    </>
  );

  const renderCompFiltersTab = () => {
    const selectedIsDefault = (effectiveFilters.admin_filters || []).some((f) => f.FilterName === selectedFilter);
    const persistFilterEdits = !selectedIsDefault || isPropAdmin;
    // Helper: check if any filter settings have changed since loading
    const settingsChanged =
      originalSettings &&
      (JSON.stringify(settings) !== JSON.stringify(originalSettings) ||
        (isPropAdmin && saveAsAdmin !== (originalSettings.saveAsAdmin ?? false)));
    const nameChanged = filterName.trim() !== selectedFilter;
    
    return (
      <>
        {/* Main layout with two columns */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, maxWidth: '100%' }}>
          {/* Left column - Filter Selection */}
          <Box sx={{ flex: '1 1 280px', minWidth: 0, maxWidth: '100%', p: 2, pb:0, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', mb:0, alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ mb: 0, pb:0 }}>Filter Selection</Typography>
              <IconButton
                aria-label="delete filter"
                color="error"
                size="small"
                onClick={handleDeleteFilter}
                sx={{
                  ml: 1,
                  visibility: canDeleteFilter(selectedFilter) ? 'visible' : 'hidden',
                  pointerEvents: canDeleteFilter(selectedFilter) ? 'auto' : 'none'
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0 }}>
              <FormControl sx={{ width: 200 }}>
                <Select
                              value={effectiveIsLoadingFilters ? '' : selectedFilter}
                onChange={(e) => handleFilterSelection(e.target.value)}
                size="small"
                sx={{ 
                  height: '40px',
                  fontSize: '0.875rem'
                }}
                disabled={effectiveIsLoadingFilters}
                displayEmpty
                renderValue={(value) => {
                  if (effectiveIsLoadingFilters) {
                    return <span style={{ color: '#666' }}>Loading...</span>;
                  }
                  return value || 'Select a filter';
                }}
                  >
                    <MenuItem value="new">Create New Filter</MenuItem>
                    {/* Default Filters */}
                    {(effectiveFilters.admin_filters || []).length > 0 && (
                      <ListSubheader>Default Filters</ListSubheader>
                    )}
                    {(effectiveFilters.admin_filters || []).map((filter) => (
                      <MenuItem key={filter.FilterName} value={filter.FilterName}>
                        {filter.FilterName}
                      </MenuItem>
                    ))}
                    {/* User Filters */}
                    {(effectiveFilters.user_filters || []).length > 0 && (
                      <ListSubheader>User Filters</ListSubheader>
                    )}
                    {(effectiveFilters.user_filters || []).map((filter) => (
                      <MenuItem key={filter.FilterName} value={filter.FilterName}>
                        {filter.FilterName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(selectedFilter === 'new' || (selectedFilter && selectedFilter !== 'new')) && (
                  <TextField
                    label="Filter Name"
                    value={filterName}
                    onChange={(e) => {
                      setFilterName(e.target.value);
                      setFilterChanged(true);
                    }}
                    disabled={selectedIsDefault && !isPropAdmin}
                    size="small"
                    sx={{ 
                      height: '40px',
                      fontSize: '0.875rem',
                      width: 200,
                      '& .MuiInputBase-root': {
                        height: '40px'
                      }
                    }}
                    placeholder="Enter filter name..."
                  />
                )}
              </Box>
                {/* Public Filter checkbox — only a Propriety admin can save a default filter */}
                <Box sx={{ mt: 0, ml: 0.5, width: '100%' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={saveAsAdmin}
                        onChange={(e) => {
                          setSaveAsAdmin(e.target.checked);
                          setFilterChanged(true);
                        }}
                        disabled={!isPropAdmin}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        Public Filter
                        <Tooltip title={isPropAdmin ? "This filter will be visible for all users to select from." : "Only Propriety admins can edit default filters."} placement="right">
                          <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5 }} />
                        </Tooltip>
                      </Box>
                    }
                    size="small"
                    sx={{ mt: 0, width: '100%' }}
                  />
                </Box>

              {/* Error Display */}
              {filterError && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: 'error.contrastText' }}>
                    {filterError}
                  </Typography>
                </Box>
              )}

              {/* Comp Filtering Options */}
              <Typography variant="subtitle2" sx={{ mb: 0, fontWeight: 600 }}>Comp Filtering:</Typography>
              <FormControl component="fieldset">
                <FormGroup row>
                  {[
                    { value: 'mlsOnly', label: 'Only MLS Comps' },
                  ].map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={settings.compFilters?.includes(option.value) || false}
                          onChange={(e) => {
                            const currentValues = settings.compFilters;
                            const newValues = e.target.checked
                              ? [...currentValues, option.value]
                              : currentValues.filter((val) => val !== option.value);
                            handleFieldChange('compFilters', newValues);
                          }}
                        />
                      }
                      label={option.label}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>

            {/* Right column - Date Range Filtering */}
            <Box sx={{ flex: '1 1 280px', minWidth: 0, maxWidth: '100%', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Date Range Filtering</Typography>
              
              {/* Evaluation Date and Date Range Display on same line */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 2, width: 200, minWidth: 200, flexShrink: 0 }}>
                  Evaluation Date: {isLoadingEvalDate ? 'Loading...' : (evalDate ? dayjs(evalDate).format('MM/DD/YYYY') : 'Not available')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0}}>
                    Current Date Range:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {(
                      settings.beforePreset === 'nocutoff' || !settings.dateRange?.[0]
                        ? 'No Cutoff'
                        : dayjs(settings.dateRange[0]).format('MM/DD/YYYY')
                    )}
                    {' - '}
                    {(
                      settings.afterPreset === 'nolimit' || !settings.dateRange?.[1]
                        ? 'No Limit'
                        : dayjs(settings.dateRange[1]).format('MM/DD/YYYY')
                    )}
                  </Typography>
                </Box>
              </Box>

              {/* Date Range Controls */}
              {settings.dateRangeType === 'preset' ? (
                /* Preset Date Range Selection */
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 0 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: '200px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0 }}>Before Eval Date</Typography>
                    <Select
                      value={settings.beforePreset}
                      onChange={(e) => handleFieldChange('beforePreset', e.target.value)}
                      size="small"
                      sx={{ 
                        height: '32px',
                        fontSize: '0.875rem'
                      }}
                      displayEmpty
                    >
                      {DATE_RANGE_PRESETS.BEFORE.map((preset) => (
                        <MenuItem key={preset.value} value={preset.value}>
                          {preset.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '20px',
                    mt: 3
                  }}>
                    <Typography variant="h6" sx={{ color: '#666' }}>-</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: '200px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0 }}>After Eval Date</Typography>
                    <Select
                      value={settings.afterPreset}
                      onChange={(e) => handleFieldChange('afterPreset', e.target.value)}
                      size="small"
                      sx={{ 
                        height: '32px',
                        fontSize: '0.875rem'
                      }}
                      displayEmpty
                    >
                      {DATE_RANGE_PRESETS.AFTER.map((preset) => (
                        <MenuItem key={preset.value} value={preset.value}>
                          {preset.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
              ) : (
                /* Custom Date Range Selection */
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 0, pb:0}}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, pb:0, minWidth: '200px', alignSelf: 'flex-start' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0 }}>Earliest Sale Date</Typography>
                    <DatePicker
                      selected={settings.dateRange?.[0] ? new Date(settings.dateRange[0] + 'T00:00:00') : null}
                      onChange={(date) => {
                        const formattedDate = date ? date.toISOString().split('T')[0] : null;
                        handleFieldChange('dateRange', [formattedDate, settings.dateRange?.[1]]);
                      }}
                      selectsStart
                      startDate={settings.dateRange?.[0] ? new Date(settings.dateRange[0] + 'T00:00:00') : null}
                      endDate={settings.dateRange?.[1] ? new Date(settings.dateRange[1] + 'T00:00:00') : null}
                      isClearable
                      dateFormat="MM/dd/yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      popperPlacement="bottom-start"
                      maxDate={new Date()}
                      autoComplete="off"
                      customInput={<CustomDateInput 
                        placeholder="MM/DD/YYYY" 
                        style={{ height: '32px' }}
                        onComplete={() => {
                          setTimeout(() => {
                            if (endDateRef.current) {
                              endDateRef.current.focus();
                            }
                          }, 100);
                        }} 
                      />}
                    />
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '20px',
                    mt: 3
                  }}>
                    <Typography variant="h6" sx={{ color: '#666' }}>-</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: '200px', alignSelf: 'flex-start' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0 }}>Last Sale Date Cutoff</Typography>
                    <DatePicker
                      selected={settings.dateRange?.[1] ? new Date(settings.dateRange[1] + 'T00:00:00') : null}
                      onChange={(date) => {
                        const formattedDate = date ? date.toISOString().split('T')[0] : null;
                        handleFieldChange('dateRange', [settings.dateRange?.[0], formattedDate]);
                      }}
                      selectsEnd
                      startDate={settings.dateRange?.[0] ? new Date(settings.dateRange[0] + 'T00:00:00') : null}
                      endDate={settings.dateRange?.[1] ? new Date(settings.dateRange[1] + 'T00:00:00') : null}
                      minDate={settings.dateRange?.[0] ? new Date(settings.dateRange[0] + 'T00:00:00') : null}
                      isClearable
                      dateFormat="MM/dd/yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      popperPlacement="bottom-start"
                      maxDate={new Date()}
                      autoComplete="off"
                      customInput={<EndDateInput 
                        placeholder="MM/DD/YYYY" 
                        style={{ height: '32px' }}
                        ref={endDateRef}
                      />}
                    />
                  </Box>
                </Box>
              )}

              {/* Custom Date Range Checkbox below date pickers */}
              <Box sx={{ mb: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={settings.dateRangeType === 'custom'}
                      onChange={(e) => handleFieldChange('dateRangeType', e.target.checked ? 'custom' : 'preset')}
                      size="small"
                    />
                  }
                  label="Use Custom Date Range"
                  sx={{ 
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Tabular Selection */}
            <Box sx={{ pb:0 }}>
              {/* Feature Cutoffs Section */}
              <Box sx={{ }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Feature Cutoffs</Typography>
                  <Tooltip 
                    title={
                      <>
                        These cutoffs denote the percentage a comp can be relative to the subject. ie: Acres of 15% means a comp will show up if its 20% of the subject acres, but will not show up if it is 14%.
                        <br/> <br/>
                        For example: if a subject is 1 acre, and the cutoff is 20%, comps below 0.2 acres will not be shown.
                      </>
                    } 
                    placement="top"
                  >
                    <IconButton size="small" sx={{ padding: '2px' }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={settings.squareFeetFilterEnabled}
                              onChange={(e) => handleFieldChange('squareFeetFilterEnabled', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Square Feet Filter: {settings.squareFeetFilterEnabled ? `${settings.squareFeetFilter}%` : 'Disabled'}
                            </Typography>
                          }
                        />
                      </Box>
                      <Slider
                        value={settings.squareFeetFilterEnabled ? settings.squareFeetFilter : 0}
                        onChange={(e, newValue) => handleFieldChange('squareFeetFilter', newValue)}
                        min={0}
                        max={100}
                        step={5}
                        disabled={!settings.squareFeetFilterEnabled}
                        marks={[
                          { value: 0, label: '0%' },
                          { value: 50, label: '50%' },
                          { value: 100, label: '100%' }
                        ]}
                        sx={{ 
                          width: '90%',
                          '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem'
                          },
                          '& .MuiSlider-mark': {
                            width: '2px',
                            height: '8px'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={settings.acreageFilterEnabled}
                              onChange={(e) => handleFieldChange('acreageFilterEnabled', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Acreage Filter: {settings.acreageFilterEnabled ? `${settings.acreageFilter}%` : 'Disabled'}
                            </Typography>
                          }
                        />
                      </Box>
                      <Slider
                        value={settings.acreageFilterEnabled ? settings.acreageFilter : 0}
                        onChange={(e, newValue) => handleFieldChange('acreageFilter', newValue)}
                        min={0}
                        max={100}
                        step={5}
                        disabled={!settings.acreageFilterEnabled}
                        marks={[
                          { value: 0, label: '0%' },
                          { value: 50, label: '50%' },
                          { value: 100, label: '100%' }
                        ]}
                        sx={{ 
                          width: '90%',
                          '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem'
                          },
                          '& .MuiSlider-mark': {
                            width: '2px',
                            height: '8px'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={settings.grossAdjPctFilterEnabled}
                              onChange={(e) => handleFieldChange('grossAdjPctFilterEnabled', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Gross Adjustment % Filter: {settings.grossAdjPctFilterEnabled ? `${settings.grossAdjPctFilter}%` : 'Disabled'}
                            </Typography>
                          }
                        />
                        <Tooltip title="Do not display a comp if its adjustments are more than x% of the sale price" placement="top">
                          <IconButton size="small" sx={{ padding: '2px' }}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Slider
                        value={settings.grossAdjPctFilterEnabled ? settings.grossAdjPctFilter : 0}
                        onChange={(e, newValue) => handleFieldChange('grossAdjPctFilter', newValue)}
                        min={0}
                        max={100}
                        step={5}
                        disabled={!settings.grossAdjPctFilterEnabled}
                        marks={[
                          { value: 0, label: '0%' },
                          { value: 50, label: '50%' },
                          { value: 100, label: '100%' }
                        ]}
                        sx={{ 
                          width: '90%',
                          '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem'
                          },
                          '& .MuiSlider-mark': {
                            width: '2px',
                            height: '8px'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={settings.netAdjPctFilterEnabled}
                              onChange={(e) => handleFieldChange('netAdjPctFilterEnabled', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Net Adjustment % Filter: {settings.netAdjPctFilterEnabled ? `${settings.netAdjPctFilter}%` : 'Disabled'}
                            </Typography>
                          }
                        />
                        <Tooltip title="Do not display a comp if its net adjustments are more than x% of the sale price" placement="top">
                          <IconButton size="small" sx={{ padding: '2px' }}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Slider
                        value={settings.netAdjPctFilterEnabled ? settings.netAdjPctFilter : 0}
                        onChange={(e, newValue) => handleFieldChange('netAdjPctFilter', newValue)}
                        min={0}
                        max={100}
                        step={5}
                        disabled={!settings.netAdjPctFilterEnabled}
                        marks={[
                          { value: 0, label: '0%' },
                          { value: 50, label: '50%' },
                          { value: 100, label: '100%' }
                        ]}
                        sx={{ 
                          width: '90%',
                          '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem'
                          },
                          '& .MuiSlider-mark': {
                            width: '2px',
                            height: '8px'
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Save/Update Button at bottom right, with Close */}
          <div className="absolute right-2 bottom-2 justify-end">
            <Button onClick={handleClose} disabled={isSavingFilter}>Close</Button>
            <Tooltip title={!persistFilterEdits && settingsChanged ? "Only Propriety admins can edit default filters" : (filterChanged ? "Save filter changes first" : "")}>
              <span>
                <Button
                  onClick={async () => {
                    if (settingsUpdated) { setSettingsUpdated(false); }
                    if (!selectedFilter) {
                      // No filter selected, do nothing
                      return;
                    } else if (selectedFilter === 'new') {
                      // When creating a new filter, always save to database
                      const success = await handleSaveFilter(isPropAdmin && saveAsAdmin);
                      if (success) {
                        handleClose();
                      }
                    } else {
                      if (nameChanged && !settingsChanged) {
                        // Only the name changed: save locally and close
                        setSelectedFilter(filterName.trim());
                        setCurrentFilter(filterName.trim());
                        if (onClose) onClose();
                      } else if (settingsChanged && persistFilterEdits) {
                        // Filter settings have changed: save to database
                        const success = await handleSaveFilter(isPropAdmin && saveAsAdmin);
                        if (success) {
                          handleClose();
                        }
                      } else {
                        // Just save the settings locally without updating the database
                        savePage();
                        toast.success('User settings saved successfully!');
                        handleClose();
                      }
                    }
                  }}
                  variant="contained"
                  disabled={(!selectedFilter || (selectedFilter === 'new' && !filterName.trim())) || isSavingFilter}
                  startIcon={isSavingFilter ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {isSavingFilter
                    ? 'Saving...'
                    : (!selectedFilter
                      ? 'Select Filter'
                      : (selectedFilter === 'new'
                        ? 'Create'
                        : (nameChanged && !settingsChanged
                          ? 'Save'
                          : (settingsChanged && persistFilterEdits ? 'Edit Filter' : 'Save'))))}
                </Button>
              </span>
            </Tooltip>
          </div>
        </>
      );
    };

  return (
    <StableMuiDialog open={open} 
      onClose={(event, reason) => {
        if (reason && reason === "backdropClick") return;
        handleClose();
      }}
      fullWidth maxWidth="xl">
      <DialogTitle>
        Advanced Settings - {urlParams.MuniCode} {urlParams.TaxYear}
      </DialogTitle>
      
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        sx={{
          '& .MuiTab-root': {
            opacity: 1, // Ensure non-disabled tabs are fully visible
          },
          '& .MuiTab-root.Mui-disabled': {
            backgroundColor: 'transparent !important', // Remove background for disabled tabs
            color: '#9e9e9e !important',
          },
        }}
        >
            <Tab label="General" />
            <Tab label="Comp Filters" />
            <Tab label="Offer Options" />
            <Tab label="Adjustments" disabled={urlParams.MuniCode === 'All'} />
            {showWeights && <Tab label="Model Weights" />}
      </Tabs>

      <DialogContent sx={{ pt: 0, pb: 7, overflowX: 'hidden', maxWidth: '100%' }}>
        {activeTab === 0 && renderSalesAnalysisTab()}
        {activeTab === 1 && renderCompFiltersTab()}
        {activeTab === 2 && <OfferOptionsTab draft={offerOptionsDraft} setDraft={setOfferOptionsDraft} />}
        {activeTab === 3 && (
            <AdjustmentsDialog
              open={true} // Always render this within the tab
              onClose={() => handleClose()} // Switch back to the first tab on close
              muniCode={urlParams.MuniCode}
              taxYear={urlParams.TaxYear}
              currentParcelId={currentParcelId}
              resetToDefaultTab={() => handleSetTab(0)}
              // onClose={() => setActiveTab(0)} // Switch back to the first tab on close
              // onConfirm
            />
          //   <NewAdjustmentDisplay
          //   muniCode={urlParams.MuniCode}
          //   taxYear={urlParams.TaxYear}
          //   onClose={handleClose}
          // />
        )}

        {activeTab === 4 && showWeights && (
            <WeightsViewRebuilt
              open={true} // Always render this within the tab
              onClose={() => handleClose()} // Switch back to the first tab on close
              runAnalysis={runAnalysis}
            />
        )}
        {/* {activeTab !== 0 && <Typography variant="body2">This tab is under construction.</Typography>} */}
        
        {/* Simple Save buttons for tabs that don't involve filter management */}
        {(activeTab !== 3 && activeTab !== 4 && activeTab !== 1) && (
          <div className="absolute right-2 bottom-2 justify-end">
            <Button onClick={handleClose}>Close</Button>
            <Button
              onClick={() => {
                if (settingsUpdated) { setSettingsUpdated(false); }
                if (activeTab === 2) {
                  saveOfferOptions();
                } else {
                  savePage();
                }
                handleClose();
              }}
              variant="contained"
              disabled={!settingsUpdated}
            >
              Save
            </Button>
          </div>
        )}
      </DialogContent>

      {/* Override Warning Modal */}
      <StableMuiDialog
        open={showOverrideWarning}
        onClose={() => setShowOverrideWarning(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
          ⚠️ Override Manually Reviewed Comps
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to enable the override for manually reviewed comps. This means:
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2 }}>
            • <strong>Existing manually reviewed comps will be overwritten</strong> when running review
            <br />
            • This action cannot be easily undone
            <br />
            • Make sure you understand the implications before proceeding
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowOverrideWarning(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              handleFieldChange('manualReviewOverride', 1);
              setShowOverrideWarning(false);
            }}
            color="error"
            variant="contained"
          >
            Enable Override
          </Button>
        </DialogActions>
      </StableMuiDialog>

    </StableMuiDialog>
  );
}

export default React.memo(AdvancedSettings);